import { useCallback } from 'react';
import { toaster } from '@optiaxiom/react';
import { Node, useReactFlow } from '@xyflow/react';

import { Step, Trigger } from '@frontend/types/workflow-agent';

import { wouldExceedNestingLimit } from '../utils/forLoopNestingUtils';

type NodeData = Step | Trigger;

type DragData = {
  type: 'sidebar-item';
  nodeData: NodeData;
};

type UseWorkflowDndProps = {
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
  nodes: Node[]; // Add current nodes to detect ForLoop containers
  onDropNode: (
    nodeData: Step | Trigger,
    position: { x: number; y: number },
    forLoopContainer?: Node
  ) => void;
};

const findForLoopContainerAtPosition = (
  clientX: number,
  clientY: number,
  nodes: Node[]
): Node | null => {
  const elementAtPosition = document.elementFromPoint(clientX, clientY);
  if (!elementAtPosition) return null;

  const forLoopContainers: string[] = [];
  let currentElement: Element | null = elementAtPosition;

  while (currentElement) {
    const forLoopId = currentElement.getAttribute('data-forloop-id');
    if (forLoopId) {
      forLoopContainers.push(forLoopId);
    }
    currentElement = currentElement.parentElement;
  }

  if (forLoopContainers.length === 0) return null;

  const targetForLoopId = forLoopContainers[0];

  return nodes.find(node => node.id === targetForLoopId && node.type === 'for_loop') || null;
};

export function useWorkflowDnd({ reactFlowWrapper, nodes, onDropNode }: UseWorkflowDndProps) {
  const { screenToFlowPosition } = useReactFlow();

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();

      if (!reactFlowBounds) {
        return;
      }

      try {
        const rawData = event.dataTransfer.getData('application/json');

        if (!rawData) {
          return;
        }

        const dragData: DragData = JSON.parse(rawData);

        if (dragData.type === 'sidebar-item' && dragData.nodeData) {
          const position = screenToFlowPosition({
            x: event.clientX - reactFlowBounds.left,
            y: event.clientY - reactFlowBounds.top,
          });

          const forLoopContainer = findForLoopContainerAtPosition(
            event.clientX,
            event.clientY,
            nodes
          );

          if (
            dragData.nodeData.type === 'for_loop' &&
            wouldExceedNestingLimit(forLoopContainer, nodes)
          ) {
            toaster.create('Cannot nest ForLoop more than 4 levels deep', {
              intent: 'danger',
            });
            return;
          }

          onDropNode(dragData.nodeData, position, forLoopContainer || undefined);
        }
      } catch {
        console.warn('Failed to drop node');
      }
    },
    [screenToFlowPosition, reactFlowWrapper, nodes, onDropNode]
  );

  return {
    onDragOver,
    onDrop,
  };
}
